import { Check, Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type MessageKey, useI18n } from "@/i18n";
import { cn } from "@/lib/cn";
import { type ThemeMode, useTheme } from "@/lib/theme";
import { Button } from "./ui/button";

const OPTIONS: { mode: ThemeMode; icon: typeof Sun; label: MessageKey }[] = [
  { mode: "light", icon: Sun, label: "theme.light" },
  { mode: "dark", icon: Moon, label: "theme.dark" },
  { mode: "system", icon: Monitor, label: "theme.system" },
];

/** Light, dark or the device's own setting, from a small menu beside the language button. */
export function ThemeToggle() {
  const { t } = useI18n();
  const { mode, dark, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (
        e instanceof KeyboardEvent ? e.key === "Escape" : !box.current?.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const Icon = dark ? Moon : Sun;
  return (
    <div ref={box} className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label={t("theme.label")}
        aria-haspopup="menu"
        aria-expanded={open}
        title={t("theme.label")}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon aria-hidden />
      </Button>
      {open && (
        <div
          role="menu"
          aria-label={t("theme.label")}
          className="absolute right-0 z-50 mt-1 w-44 rounded-lg border bg-card p-1 shadow-lg"
        >
          {OPTIONS.map((o) => (
            <button
              key={o.mode}
              type="button"
              role="menuitemradio"
              aria-checked={mode === o.mode}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted",
                mode === o.mode && "font-medium",
              )}
              onClick={() => {
                setTheme(o.mode);
                setOpen(false);
              }}
            >
              <o.icon className="size-4 text-muted-foreground" aria-hidden />
              <span className="flex-1">{t(o.label)}</span>
              {mode === o.mode && <Check className="size-4 text-primary" aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
